import { useAutoAnimate } from '@formkit/auto-animate/react';
import { FireIcon, SparklesIcon } from '@heroicons/react/20/solid';
import { RectangleGroupIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import axios from 'axios';
import Tag from 'components/common/tag';
import Button from 'components/inputs/button';
import Checkbox from 'components/inputs/checkbox';
import Skeleton from 'components/layout/skeleton';
import ProductLink from 'components/products/product-link';
import ProductPhoto from 'components/products/product-photo';
import DuctedSystemMap from 'components/systems/ducted-system-map';
import {
  expandProductIds,
  useAlpineCartContext
} from 'contexts/alpine-cart-context';
import {
  RebateType,
  SelectedDuctedSystemType,
  ShippingInfoType
} from 'data/types';
import { setSelectedDuctedSystems } from 'features/systems';
import { setQuoteIDsToSend, setRebatesForModal } from 'features/ui';
import { produce } from 'immer';
import {
  Dispatch,
  MouseEvent,
  SetStateAction,
  useEffect,
  useState
} from 'react';
import { formatUSD } from 'utils/helpers';
import { errorModal, notification, warningModal } from 'utils/ui';

type DuctedQuoteColumnProps = {
  system: SelectedDuctedSystemType;
  systemIndex: number;
  handleQuoteColumnClick: (systemIndex: number) => void;
  setRebatesModalOpen: Dispatch<SetStateAction<boolean>>;
  optionIndex: number | null;
  setOptionIndex: Dispatch<SetStateAction<number | null>>;
};

export default function DuctedQuoteColumn({
  system,
  systemIndex,
  handleQuoteColumnClick,
  setRebatesModalOpen,
  optionIndex,
  setOptionIndex
}: DuctedQuoteColumnProps) {
  const dispatch = useAppDispatch();
  const { locationData, projectDetails } = useAppSelector(
    (state) => state.project
  );
  const { selectedDuctedSystems } = useAppSelector((state) => state.systems);
  const { quoteIDsToSend } = useAppSelector((state) => state.ui);
  const [showAllItems, setShowAllItems] = useState(false);
  const [animate] = useAutoAnimate({ duration: 150 });

  const { pasteToCart, isLoading: isAddingToCart } = useAlpineCartContext();

  const getTax = async () => {
    if (locationData) {
      const payload = JSON.stringify({
        destinationZip: locationData.zipcode,
        shippingcost: 0,
        products: [
          ...system.products.map((p) => ({
            id: p.productId,
            quantity: p.quantity
          })),
          ...system.quotableAccessories.map((acc) => ({
            id: acc.productId,
            quantity: acc.quantity
          }))
        ]
      });
      try {
        const newTax = (
          await axios.post(
            `${process.env.REACT_APP_PARTNERNET_API_URL}/v1/api/cart/tax`,
            payload
          )
        ).data as number;
        return newTax;
      } catch (error) {
        console.error(error);
        errorModal('Unable to calculate tax.');
        return null;
      }
    }
    return null;
  };

  const getShippingInfo = async () => {
    if (locationData) {
      const payload = JSON.stringify({
        zipcode: locationData.zipcode,
        products: [
          ...system.products.map((p) => ({
            id: p.productId,
            qty: p.quantity
          })),
          ...system.quotableAccessories.map((acc) => ({
            id: acc.productId,
            qty: acc.quantity
          }))
        ]
      });
      try {
        const newShippingInfo = (
          await axios.post(
            `${process.env.REACT_APP_PARTNERNET_API_URL}/v1/api/shipping/info`,
            payload
          )
        ).data as ShippingInfoType;
        return newShippingInfo;
      } catch (error) {
        console.error(error);
        errorModal('Unable to get shipping information.');
        return null;
      }
    }
    return null;
  };

  const getRebates = async () => {
    if (locationData) {
      const rebateInfo: RebateType[] = [];

      const productData = [
        ...system.products.map((p) => ({
          product_id: p.productId,
          product_price: system.price / 2
        }))
      ];

      const payload = {
        zipCode: locationData.zipcode,
        productsData: productData
      };

      try {
        const rebateResponse = (
          await axios.post(
            `${process.env.REACT_APP_PARTNERNET_API_URL}/v1/api/upfront/get_rebates`,
            payload
          )
        ).data.data;
        rebateResponse.forEach((rebate) => {
          rebateInfo.push({
            productId: Number(rebate.id),
            rebateMaximum: rebate.rebateMaximum,
            rebateMinimum: rebate.rebateMinimum,
            programs: rebate.programs,
            price: rebate.price,
            zipcode: locationData.zipcode,
            productName: rebate.productName
          });
        });
        return rebateInfo;
      } catch (error) {
        console.error(error);
        errorModal('Unable to get rebate information.');
        return null;
      }
    }
    return null;
  };

  useEffect(() => {
    if (optionIndex !== null) {
      setOptionIndex(optionIndex);
    }
  }, [optionIndex]);

  useEffect(() => {
    if (!system.tax || !system.shipping) {
      const taxPromise = getTax();
      const shippingPromise = getShippingInfo();
      const rebatesPromise = getRebates();
      Promise.all([taxPromise, shippingPromise, rebatesPromise]).then(
        ([taxInfo, shippingInfo, rebatesInfo]) => {
          let totalRebates = 0;
          if (rebatesInfo) {
            const productQuantities = system.products
              .map((p) => ({ id: p.productId, quantity: p.quantity }))
              .reduce((acc, product) => {
                if (product.id) {
                  acc[product.id] = (acc[product.id] || 0) + product.quantity;
                }
                return acc;
              }, {});

            totalRebates = rebatesInfo.reduce((sum, rebate) => {
              const quantity = productQuantities[rebate.productId] || 0;
              return (
                sum +
                Math.max(rebate.rebateMinimum, rebate.rebateMaximum) * quantity
              );
            }, 0);
          }
          dispatch(
            setSelectedDuctedSystems(
              produce(selectedDuctedSystems, (prevSystems) =>
                prevSystems.map((s) =>
                  s.id === system.id
                    ? {
                        ...s,
                        tax: taxInfo ?? undefined,
                        shipping: shippingInfo ?? undefined,
                        rebates: rebatesInfo ?? undefined,
                        totalRebates
                      }
                    : s
                )
              )
            )
          );
        }
      );
    }
  }, []);

  const handleColumnClick = () => {
    if (!quoteIDsToSend.includes(system.id.toString())) {
      if (quoteIDsToSend.length >= 3) {
        warningModal('You can only include up to 3 systems per quote');
        return;
      }
      dispatch(setQuoteIDsToSend([...quoteIDsToSend, system.id.toString()]));
    } else {
      dispatch(
        setQuoteIDsToSend(
          quoteIDsToSend.filter((id) => id !== system.id.toString())
        )
      );
    }
  };

  const handleShowAllItems = () => {
    setShowAllItems((oldValue) => !oldValue);
  };

  const handleRebatesClick = (event?: MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    dispatch(
      setRebatesForModal(
        system.rebates?.filter((rebate) => rebate.programs.length > 0) ?? []
      )
    );
    setRebatesModalOpen(true);
    handleQuoteColumnClick(systemIndex);
  };

  const handleBuyNow = () => {
    const productsWithQuantities: Array<{ id: string; qty: number }> = [
      ...system.products
        .filter((p) => p.productId)
        .map((p) => ({
          id: p.productId.toString(),
          qty: p.quantity
        })),
      ...system.quotableAccessories
        .filter((acc) => acc.quantity > 0 && acc.productId)
        .map((acc) => ({
          id: acc.productId.toString(),
          qty: acc.quantity
        }))
    ];

    if (productsWithQuantities.length === 0) {
      notification('No products to add to cart', 'warning');
      return;
    }

    const expandedProductIds = expandProductIds(productsWithQuantities);

    pasteToCart(expandedProductIds, {
      clearCart: true,
      redirectUrl: `${process.env.REACT_APP_ALPINE_CLIENT_URL}/cart`,
      onError: (error) => {
        errorModal(`Failed to add items to cart: ${error}`);
      }
    });
  };

  return system ? (
    <div
      data-active={quoteIDsToSend.includes(system.id.toString())}
      className='flex w-full flex-col rounded-lg border-4 border-white bg-white p-5 shadow-lg ring-1 ring-black/5 transition-all data-[active=true]:border-blue-500'
    >
      <button
        type='button'
        className='flex cursor-pointer flex-col items-center border-gray-200 text-left outline-none focus:outline-none'
        onClick={handleColumnClick}
        ref={animate}
      >
        <div className='flex w-full flex-col gap-5 border-b pb-5'>
          <div className='relative flex items-center justify-between'>
            <div className='inline-block' onClick={(e) => e.stopPropagation()}>
              <Checkbox
                value={quoteIDsToSend.includes(system.id.toString())}
                onChange={() => handleColumnClick()}
              />
            </div>
            {system.inStock && <Tag color='green' text='In Stock' />}
            <div className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xl font-bold'>
              Option{' '}
              {selectedDuctedSystems.findIndex((s) => s.id === system.id) + 1}
            </div>
          </div>
          <div className='flex flex-col'>
            <div className='text-center text-4xl font-bold'>
              {formatUSD(
                system.price +
                  system.quotableAccessories.reduce(
                    (total, acc) => total + acc.price * acc.quantity,
                    0
                  ) +
                  (projectDetails?.customerTaxExempt ? 0 : system.tax ?? 0),
                true
              )}
            </div>
            {projectDetails?.customerTaxExempt ? (
              <div className='text-center font-semibold text-gray-600'>
                (Tax exempt)
              </div>
            ) : (
              <div className='text-center font-semibold text-gray-500'>
                {system.tax !== undefined
                  ? `incl. ${formatUSD(system.tax, true)} tax`
                  : 'Calculating tax...'}
              </div>
            )}
          </div>
          {system.totalRebates !== undefined &&
            system.totalRebates > 0 &&
            system.rebates && (
              <div className='flex justify-center text-center'>
                <a
                  role='button'
                  className='font-semibold text-blue-500 hover:text-blue-600'
                  onClick={(e) => handleRebatesClick(e)}
                >
                  Get up to {formatUSD(Math.floor(system.totalRebates))} in
                  rebates for {locationData?.location ?? 'your location'}
                </a>
              </div>
            )}
          {!system.rebates && (
            <div className='flex justify-center text-center'>
              <div className='w-[350px]'>
                <Skeleton color='blue' />
              </div>
            </div>
          )}
        </div>
        <div className='flex w-full flex-col gap-3 border-b py-5'>
          {system.shipping
            ? [
                ...system.shipping.shipOptions.rates.filter(
                  (r) => r.type === 'standard'
                ),
                ...system.shipping.shipOptions.rates
                  .filter((r) => r.type === 'guaranteed')
                  .sort(
                    (a, b) =>
                      new Date(a.deliverydate).getTime() -
                      new Date(b.deliverydate).getTime()
                  )
              ]
                .slice(0, 2)
                .map((option) => {
                  const location = locationData?.location
                    ? `${locationData.location} ${locationData.zipcode}`
                    : option.calculatedFor;
                  const name =
                    option.type === 'standard'
                      ? `Standard Shipping for ${location}`
                      : `Express Shipping for ${location}`;
                  const deliveryDate = new Date(
                    option.deliverydate
                  ).toLocaleDateString('en-us', {
                    weekday: 'long',
                    month: 'numeric',
                    day: 'numeric'
                  });
                  const timeframe =
                    option.label.match(/[\w\d/]+ - [\w\d/]+/gi)?.[0];
                  const formattedTimeframe = timeframe
                    ?.split(' - ')
                    .map((date) =>
                      new Date(date).toLocaleDateString('en-us', {
                        month: 'numeric',
                        day: 'numeric'
                      })
                    )
                    .join(' - ');
                  // eslint-disable-next-line no-nested-ternary
                  const description = !timeframe
                    ? `Arrives by ${deliveryDate}`
                    : option.type === 'standard'
                    ? `Arrives between ${formattedTimeframe ?? timeframe}`
                    : `Arrives by ${deliveryDate} (${timeframe})`;

                  return (
                    <div
                      key={option.id}
                      className='flex items-center justify-between gap-3'
                    >
                      <div>
                        <div className='mb-1 font-semibold'>{name}</div>
                        <div className='text-sm text-gray-600'>
                          {description}
                        </div>
                      </div>
                      <div className='text-lg font-bold'>
                        {option.cost > 0
                          ? formatUSD(option.cost, true)
                          : 'Free'}
                      </div>
                    </div>
                  );
                })
            : [1, 2].map((i) => (
                <div key={i} className='flex items-center justify-between'>
                  <div>
                    <div className='mb-1 w-[300px]'>
                      <Skeleton />
                    </div>
                    <div className='w-[200px]'>
                      <Skeleton />
                    </div>
                  </div>
                  <div className='w-[50px]'>
                    <Skeleton />
                  </div>
                </div>
              ))}
        </div>
        <div className='flex w-full flex-col gap-2 border-b py-5'>
          <div className='text-lg font-bold'>Annual Coverage</div>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <FireIcon className='h-5 w-5 text-orange-500' />
              <span className='text-gray-600'>Heating</span>
            </div>
            <div className='relative'>
              <div className='flex items-center gap-1'>
                <span className='font-semibold'>100%</span>
              </div>
            </div>
          </div>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <SparklesIcon className='h-5 w-5 text-sky-500' />
              <span className='text-gray-600'>Cooling</span>
            </div>
            <div className='relative'>
              <div className='flex items-center gap-1'>
                <span className='font-semibold'>100%</span>
              </div>
            </div>
          </div>
        </div>
        <div className='flex w-full flex-col gap-2 border-b py-5'>
          <div className='text-lg font-bold'>Warranty Information</div>
          <div className='flex items-center justify-between'>
            <span className='text-gray-600'>Parts</span>
            <span className='font-semibold'>5 years</span>
          </div>
          <div className='flex items-center justify-between'>
            <span className='text-gray-600'>Compressor</span>
            <span className='font-semibold'>7 years</span>
          </div>
        </div>
        <div className='flex w-full flex-col gap-5 py-5'>
          {system.config.condensers.map((condenser) => (
            <DuctedSystemMap
              key={condenser.uniqueId}
              system={system}
              condenser={condenser}
              showPrices
            />
          ))}
        </div>
        {showAllItems &&
          system.quotableAccessories.some((acc) => acc.quantity > 0) && (
            <div className='flex w-full flex-col gap-5 pb-5'>
              <div className='flex items-center gap-2'>
                <RectangleGroupIcon className='h-6 w-6 text-gray-800' />
                <span className='text-xl font-semibold'>Accessories</span>
              </div>
              <div className='flex flex-col gap-7'>
                {system.quotableAccessories
                  .filter((acc) => acc.quantity > 0)
                  .map((accessory) => (
                    <div
                      key={accessory.productId}
                      className='flex items-center gap-4'
                    >
                      <ProductPhoto product={accessory} />
                      <div className='flex grow flex-col gap-1'>
                        <ProductLink
                          type='accessory'
                          product={accessory}
                          quantity={accessory.quantity}
                        />
                        <span className='text-xs text-gray-600'>
                          {accessory.description}
                        </span>
                      </div>
                      <div className='pl-3 text-lg font-bold'>
                        {formatUSD(accessory.price * accessory.quantity, true)}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
      </button>
      <div className='flex flex-col gap-5 pt-5'>
        {system.quotableAccessories.some((acc) => acc.quantity > 0) && (
          <Button
            text={showAllItems ? 'Show Fewer Items' : 'Show All Items'}
            color='secondary'
            size='lg'
            fullWidth
            onClick={handleShowAllItems}
          />
        )}
        <Button
          text={isAddingToCart ? 'Adding to Cart...' : 'Buy Now'}
          size='lg'
          fullWidth
          onClick={handleBuyNow}
          disabled={isAddingToCart}
        />
      </div>
    </div>
  ) : null;
}
